import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  type QuerySnapshot,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from './firebase';
import type { CollaborativeNote, UserProfile } from './types';

/**
 * Collaborative notes: `collaborativeNotes/{noteId}`, shared between an owner and their
 * collaborators. See `CollaborativeNote` in types.ts for the shape and what iOS requires.
 */

const notes = collection(db, 'collaborativeNotes');

function toNotes(snap: QuerySnapshot): CollaborativeNote[] {
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as CollaborativeNote);
}

/** Newest edit first. A pending server timestamp sorts as now, which is what it is. */
function byUpdated(a: CollaborativeNote, b: CollaborativeNote): number {
  const at = a.updatedAt?.toMillis() ?? Date.now();
  const bt = b.updatedAt?.toMillis() ?? Date.now();
  return bt - at;
}

/**
 * Live view of every collaborative note the user belongs to: the ones they own, and the ones
 * shared with them.
 *
 * Two queries, as on iOS (`listenToOwnedNotes`, `listenToCollaborativeNotes`), because the
 * rules allow reads only to members and a query must be provably within that: `ownerId ==
 * uid` proves ownership, `collaboratorIds array-contains uid` proves membership. A single
 * query over both is not expressible as one the rules will accept. Both are backed by
 * existing composite indexes.
 *
 * An owner is never in their own `collaboratorIds`, so the two lists do not overlap; the
 * merge still de-duplicates by id rather than relying on it.
 */
export function useCollabNotes(uid: string | undefined) {
  const [owned, setOwned] = useState<CollaborativeNote[] | null>(null);
  const [shared, setShared] = useState<CollaborativeNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOwned(null);
    setShared(null);
    setError(null);
    if (!uid) {
      setOwned([]);
      setShared([]);
      return;
    }
    // Either failing is shown; neither hides the other's results.
    const fail = (err: Error) => setError(err.message);
    const unsubOwned = onSnapshot(
      query(notes, where('ownerId', '==', uid), orderBy('updatedAt', 'desc')),
      (snap) => setOwned(toNotes(snap)),
      (err) => {
        setOwned([]);
        fail(err);
      }
    );
    const unsubShared = onSnapshot(
      query(notes, where('collaboratorIds', 'array-contains', uid), orderBy('updatedAt', 'desc')),
      (snap) => setShared(toNotes(snap)),
      (err) => {
        setShared([]);
        fail(err);
      }
    );
    return () => {
      unsubOwned();
      unsubShared();
    };
  }, [uid]);

  const loading = owned === null || shared === null;
  const merged = new Map<string, CollaborativeNote>();
  for (const note of [...(owned ?? []), ...(shared ?? [])]) merged.set(note.id, note);

  return { notes: [...merged.values()].sort(byUpdated), error, loading };
}

/**
 * Live view of one collaborative note.
 *
 * `note` is null both before the first snapshot and when the document does not exist — or
 * when it exists and the user is not a member, which the rules report as permission-denied
 * rather than as absence. `loading` and `error` tell the cases apart.
 */
export function useCollabNote(id: string | undefined) {
  const [note, setNote] = useState<CollaborativeNote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // True between a local save and the server confirming it, while `updatedAt` is still an
  // unresolved serverTimestamp() and reads as null.
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!id) {
      setNote(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(notes, id),
      // Without this the confirming snapshot, identical in data, is never delivered and
      // `pending` would stay true.
      { includeMetadataChanges: true },
      (snap) => {
        setNote(snap.exists() ? ({ id: snap.id, ...snap.data() } as CollaborativeNote) : null);
        setPending(snap.metadata.hasPendingWrites);
        setError(null);
        setLoading(false);
      },
      (err) => {
        setNote(null);
        // A note you were removed from, or a link you were never added to, arrives here.
        setError(
          (err as { code?: string }).code === 'permission-denied'
            ? 'You are not a member of this note.'
            : err.message
        );
        setLoading(false);
      }
    );
  }, [id]);

  return { note, error, loading, pending };
}

/** The fields any member may change. Everything else is either owner-only or not ours. */
export interface CollabEdits {
  title: string;
  description: string;
  tags: string[];
  links: string[];
}

/** Thrown when someone saved between this editor opening and its save. */
export class CollabConflict extends Error {
  constructor(public readonly editorName: string) {
    super(`${editorName} saved changes to this note after you started editing.`);
  }
}

/**
 * How the editor is named in `lastEditedByName`: the profile's names, then the account's
 * display name, then the email — the order iOS uses. A profile whose names are both blank is
 * skipped rather than producing an empty name, which iOS's version does.
 */
export function editorName(user: User, profile: UserProfile | null): string {
  const fromProfile = [profile?.firstName, profile?.lastName]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(' ');
  return fromProfile || user.displayName?.trim() || user.email || user.uid;
}

/** iOS allows 1 ms of drift between what the editor opened and what is stored. */
const SAME_EDIT_MS = 1;

/**
 * Save a member's edit to a collaborative note.
 *
 * **Content fields only, by `update`.** iOS saves by rewriting the whole document
 * (`setData(merge: false)`) from its copy, which carries the membership fields as they were
 * when the editor opened. Membership changes never bump `updatedAt`, so that save can
 * silently undo an invite or a removal made in the meantime — and for a collaborator the
 * stale fields count as changed keys, which the rules deny. Writing only the fields below
 * avoids both: no member's save here can touch membership, by construction.
 *
 * **Compare-and-set on `updatedAt`, in a transaction**, the same check iOS makes: if the
 * stored `updatedAt` is not the one the editor opened with, someone saved in between, and
 * this throws `CollabConflict` naming them instead of overwriting their work. Pass
 * `opened: null` to save anyway, after the user has been told and chosen to.
 *
 * Every field iOS's decoder requires that this write owns is always present — `tags` and
 * `links` as arrays, `lastEditedBy`/`lastEditedByName` as strings, `updatedAt` as a
 * Timestamp — so a web save cannot make the note vanish from an iPhone. `serverTimestamp()`
 * rather than the browser clock, so the next editor's comparison is against the server's
 * time and not this machine's.
 */
export async function saveCollabNote(
  id: string,
  edits: CollabEdits,
  editor: { uid: string; name: string },
  opened: Timestamp | null
): Promise<void> {
  const ref = doc(notes, id);
  const data = {
    title: edits.title.trim(),
    description: edits.description.trim(),
    tags: edits.tags,
    links: edits.links,
    lastEditedBy: editor.uid,
    lastEditedByName: editor.name,
    updatedAt: serverTimestamp(),
  };

  if (opened === null) {
    await updateDoc(ref, data);
    return;
  }

  await runTransaction(db, async (tx) => {
    const current = await tx.get(ref);
    if (!current.exists()) throw new Error('This note was deleted while you were editing it.');
    const stored = current.get('updatedAt');
    if (
      stored instanceof Timestamp &&
      Math.abs(stored.toMillis() - opened.toMillis()) > SAME_EDIT_MS
    ) {
      throw new CollabConflict(current.get('lastEditedByName') || 'Someone');
    }
    tx.update(ref, data);
  });
}

/**
 * Delete a collaborative note, for everyone on it. Owner only (`allow delete: if
 * isNoteOwner()`); a collaborator's attempt is denied by the rules.
 *
 * Photos are left in place, as Android does: they may sit under the owner's
 * `users/{uid}/images/`, and the orphan sweep collects them. Outstanding link invites are
 * left too; `acceptLinkInvite` refuses one whose note no longer exists.
 */
export async function deleteCollabNote(id: string): Promise<void> {
  await deleteDoc(doc(notes, id));
}
